import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { getAssetUrl } from "../../utils/assetUrl";
import { getCasinoApiBase } from "../../utils/apiBase";
import { resolveGameScreenLink } from "../../utils/resolveGameScreenLink";
import { mapPool, uniqueByTableName } from "../../utils/mapPool";
import ResultTable from "../../components/ResultTable";
import { GameScreenEmbed } from "../../components/GameScreenEmbed";
import { fetchNhTableList, type NhTableListRow } from "../NH/fetchNhTableList";
import { NH_GAME_PROVIDERS } from "../NH/nhMenuGameProviders";
import "./HomePage.css";

type HubTab = "notice" | "slot" | "card";
type StreakSide = "P" | "B" | null;

type SexyRoom = {
  id: string;
  tableName: string;
  player: number;
  tie: number;
  banker: number;
  winPercent: number;
  totalRound: any[];
  streakSide: StreakSide;
  streakLen: number;
};

function unwrapPredictTablePayload(resBody: any): any {
  if (resBody == null || typeof resBody !== "object") return resBody;
  const hasFlatTable =
    resBody.percentCurrent != null ||
    resBody.ai0 != null ||
    resBody.groupRoad != null ||
    Array.isArray(resBody.totalRound);
  if (hasFlatTable) return resBody;
  const inner = resBody.data;
  if (inner != null && typeof inner === "object" && !Array.isArray(inner)) {
    return inner;
  }
  return resBody;
}

function roadToSide(road: number | undefined): StreakSide | "T" {
  if (road == null) return "T";
  if ([0, 1, 2].includes(road)) return "B";
  if ([8, 9, 10].includes(road)) return "P";
  return "T";
}

function lastStreak(rounds: any[]): { side: StreakSide; len: number } {
  const sorted = [...rounds].sort(
    (a, b) => (a?.stampTime ?? 0) - (b?.stampTime ?? 0)
  );
  const sides = sorted
    .map((item) => roadToSide(item?.road))
    .filter((side): side is "P" | "B" => side === "P" || side === "B");
  if (sides.length === 0) return { side: null, len: 0 };
  const side = sides[sides.length - 1];
  let len = 0;
  for (let i = sides.length - 1; i >= 0; i -= 1) {
    if (sides[i] !== side) break;
    len += 1;
  }
  return { side, len };
}

function mapSexyRoom(row: any, idx: number): SexyRoom {
  const rounds = Array.isArray(row?.totalRound) ? row.totalRound : [];
  const streak = lastStreak(rounds);
  const forecast = Number(
    row?.percentCurrent?.Forecast ??
      row?.ai0?.percentCurrent?.Forecast ??
      row?.Forecast ??
      55
  );
  return {
    id: String(row?._id ?? row?.id ?? `sexy-${idx}`),
    tableName: String(row?.tableName ?? `C${String(idx + 1).padStart(2, "0")}`),
    player: Number(row?.roundStats?.player ?? 0),
    tie: Number(row?.roundStats?.tie ?? 0),
    banker: Number(row?.roundStats?.banker ?? 0),
    winPercent: Number.isNaN(forecast) ? 55 : Math.round(forecast),
    totalRound: rounds,
    streakSide: streak.side,
    streakLen: streak.len,
  };
}

type LiveNotice = {
  id: string;
  user: string;
  points: string;
  game: string;
};

const NOTICE_USERS = [
  "Xiaofeng",
  "Vortex88",
  "Kaito_R",
  "LunaTrace",
  "mr_zero",
  "CyberNova",
  "PhantomV",
  "Starling",
  "NeonFox",
  "Ryu99",
  "BaoKim",
  "MinhAnh",
  "HackerLee",
  "Quyen88",
  "TuanPro",
  "NgocBich",
  "AceHunter",
  "LuckyDan",
  "MaiLin",
  "ZeroDay",
  "SoiDem",
  "KimCuong",
  "ThanhLong",
  "HoaSen",
  "NightOwl",
  "PixelTom",
  "VinaAce",
  "GoldFish",
  "BaoChau",
  "KhoaVIP",
];

const NOTICE_GAMES = [
  "Quyết chiến tiền thưởng",
  "Kho báu Aztec 2",
  "Đường mạt chược",
  "Aviator",
  "Neko may mắn",
  "Thỏ May Mắn",
  "Kỳ Lân Mách Nước",
  "Kho Báu AZTEC",
  "Ban nhạc hoang dã",
  "Chiến Thắng Thần Tài",
  "Rồng May Mắn",
  "Nữ hoàng tiền thưởng",
  "Anubis",
  "Giấc mơ Ma Cao",
  "Heo Vàng",
  "Cây Tài Lộc",
  "Bàn Baccarat C01",
  "Bàn Baccarat C08",
  "Bàn Baccarat C14",
  "Sảnh Sexy VIP",
  "Wild West Gold",
  "Gates of Olympus",
  "Sweet Bonanza",
  "Đèn Aladdin",
];

function pickItem<T>(list: readonly T[]): T {
  return list[Math.floor(Math.random() * list.length)] as T;
}

function formatNoticePoints(): string {
  const n = Math.floor(1200 + Math.random() * 18800);
  return n.toLocaleString("vi-VN");
}

function createNotice(): LiveNotice {
  return {
    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    user: pickItem(NOTICE_USERS),
    points: formatNoticePoints(),
    game: pickItem(NOTICE_GAMES),
  };
}

function seedNotices(count = 8): LiveNotice[] {
  return Array.from({ length: count }, () => createNotice());
}

const SLOT_FILTERS = ["Tất cả", "Tỷ lệ cao", "Theo tên", "Đang hot"] as const;
const CARD_FILTERS = ["Tất cả", "Cầu tốt", "Bệt cái +3", "Bệt con +3"] as const;
const CARD_SORTS = ["Tỷ lệ cao nhất", "Theo số bàn", "Cầu tốt trước", "Bệt dài nhất"] as const;

function winBadge(pct: number) {
  if (pct > 90) return { label: "SUPER VIP", cls: "is-gold", caption: "WIN" };
  if (pct >= 80) return { label: "TỶ LỆ THẮNG", cls: "is-green", caption: "WIN" };
  return { label: "RỦI RO CAO", cls: "is-red", caption: "WIN" };
}

const HomePage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<HubTab>("notice");
  const [notices, setNotices] = useState<LiveNotice[]>(() => seedNotices(8));
  const [extLink, setExtLink] = useState("");
  const [extName, setExtName] = useState("GG88");
  const [extLoading, setExtLoading] = useState(true);
  const [slotHall, setSlotHall] = useState<string | null>(null);
  const [slotGames, setSlotGames] = useState<NhTableListRow[]>([]);
  const [slotFilter, setSlotFilter] = useState<(typeof SLOT_FILTERS)[number]>("Tất cả");
  const [cardFilter, setCardFilter] = useState<(typeof CARD_FILTERS)[number]>("Tất cả");
  const [cardSort, setCardSort] = useState<(typeof CARD_SORTS)[number]>("Tỷ lệ cao nhất");
  const [sexyRooms, setSexyRooms] = useState<SexyRoom[]>([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [tab, slotHall]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setNotices((prev) => [createNotice(), ...prev].slice(0, 8));
    }, 2600);
    return () => window.clearInterval(timer);
  }, []);

  const openGame = useCallback(
    (id: string, name: string, image: string) => {
      localStorage.setItem("title_img", image);
      localStorage.setItem("title_text", name);
      navigate(`/NH/table/${id}`);
    },
    [navigate]
  );

  useEffect(() => {
    let cancelled = false;
    setExtLoading(true);
    resolveGameScreenLink(["hub-link", "sexy-lobby", "default"]).then((resolved) => {
      if (cancelled) return;
      setExtLink(resolved.url);
      setExtName(resolved.name);
      setExtLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (tab !== "slot" || !slotHall) return;
    let cancelled = false;
    fetchNhTableList(slotHall).then((rows) => {
      if (!cancelled) setSlotGames(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [tab, slotHall]);

  useEffect(() => {
    if (tab !== "card") return;
    let cancelled = false;
    const token = require("js-cookie").get("access_token");
    const base = getCasinoApiBase();

    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

    axios
      .get(`${base}/predict/get-all-table`, { headers, timeout: 60000 })
      .then(async (res) => {
        const basic = uniqueByTableName(Array.isArray(res.data) ? res.data : []);
        if (cancelled) return;
        setSexyRooms(basic.map(mapSexyRoom));

        const enriched = await mapPool(basic, 6, async (row: any) => {
          const name = row?.tableName;
          if (!name) return row;
          try {
            const detailRes = await axios.get(`${base}/predict/get-table-by-name`, {
              params: { tableName: name },
              headers,
              timeout: 60000,
            });
            const detail = unwrapPredictTablePayload(detailRes.data);
            return {
              ...row,
              ...detail,
              id: row.id,
              tableName: row.tableName ?? detail?.tableName ?? name,
            };
          } catch {
            return row;
          }
        });
        if (!cancelled) setSexyRooms(enriched.map(mapSexyRoom));
      })
      .catch(() => {
        if (!cancelled) setSexyRooms([]);
      });

    return () => {
      cancelled = true;
    };
  }, [tab]);

  const visibleRooms = useMemo(() => {
    let list = [...sexyRooms];
    if (cardFilter === "Cầu tốt") list = list.filter((r) => r.winPercent >= 80);
    if (cardFilter === "Bệt cái +3") {
      list = list.filter((r) => r.streakSide === "B" && r.streakLen >= 3);
    }
    if (cardFilter === "Bệt con +3") {
      list = list.filter((r) => r.streakSide === "P" && r.streakLen >= 3);
    }
    if (cardSort === "Tỷ lệ cao nhất") {
      list.sort((a, b) => b.winPercent - a.winPercent);
    } else if (cardSort === "Cầu tốt trước") {
      list.sort((a, b) => Number(b.winPercent >= 80) - Number(a.winPercent >= 80) || b.winPercent - a.winPercent);
    } else if (cardSort === "Theo số bàn") {
      list.sort((a, b) => a.tableName.localeCompare(b.tableName, undefined, { numeric: true }));
    } else if (cardSort === "Bệt dài nhất") {
      list.sort((a, b) => b.streakLen - a.streakLen);
    }
    return list;
  }, [sexyRooms, cardFilter, cardSort]);

  const visibleSlotGames = useMemo(() => {
    const list = [...slotGames];
    if (slotFilter === "Theo tên") list.sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [slotGames, slotFilter]);

  const openHalls = sexyRooms.length;
  const goodTables = sexyRooms.filter((r) => r.winPercent >= 80).length;
  const streakTables = sexyRooms.filter((r) => r.streakLen >= 3).length;

  return (
    <main className="thn-home">
      <h1 className="thn-home__brand">_Toolhacknhacai.com_</h1>

      <div className="thn-home__stage">
        <div className="thn-home__left">
          <nav className="thn-tabs" aria-label="Danh mục">
            <button
              type="button"
              className={`thn-tabs__btn${tab === "notice" ? " is-active" : ""}`}
              onClick={() => setTab("notice")}
            >
              <img src={getAssetUrl("/assets/thn/icon-bell.svg")} alt="" width={18} height={23} />
              THÔNG BÁO
              {tab === "notice" ? <span className="thn-tabs__dot thn-tabs__dot--red" /> : null}
            </button>
            <button
              type="button"
              className={`thn-tabs__btn${tab === "slot" ? " is-active" : ""}`}
              onClick={() => {
                setTab("slot");
                setSlotHall(null);
              }}
            >
              <img src={getAssetUrl("/assets/thn/icon-slot.svg")} alt="" width={21} height={21} />
              NỔ HŨ
            </button>
            <button
              type="button"
              className={`thn-tabs__btn${tab === "card" ? " is-active" : ""}`}
              onClick={() => setTab("card")}
            >
              <img src={getAssetUrl("/assets/thn/icon-card.svg")} alt="" width={23} height={22} />
              GAME BÀI
              <span className="thn-tabs__dot thn-tabs__dot--green" />
            </button>
          </nav>

          {tab === "notice" ? (
            <section className="thn-panel thn-notice" aria-label="Thông báo">
              <h2 className="thn-panel__title">
                <span className="thn-panel__bar" />
                THÔNG BÁO
              </h2>
              <ul className="thn-notice__list">
                {notices.map((item) => (
                  <li key={item.id} className="thn-notice__item">
                    <span>&gt;_ </span>
                    <b>{item.user}</b>
                    <span> vừa thắng </span>
                    <b>{item.points} điểm</b>
                    <span> tại Game </span>
                    <b>{item.game}</b>
                    <span>...</span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {tab === "slot" ? (
            <section className="thn-panel thn-slot" aria-label="Nổ hũ">
              {slotHall ? (
                <>
                  <div className="thn-slot__toolbar">
                    <button type="button" className="thn-back" onClick={() => setSlotHall(null)}>
                      ◂ QUAY LẠI
                    </button>
                    <div className="thn-sexy__chips">
                      {SLOT_FILTERS.map((item) => (
                        <button
                          key={item}
                          type="button"
                          className={`thn-sexy__chip${slotFilter === item ? " is-active" : ""}`}
                          onClick={() => setSlotFilter(item)}
                        >
                          {item}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="thn-hot__grid">
                    {visibleSlotGames.map((game) => (
                      <button
                        key={game._id}
                        type="button"
                        className="thn-hot__card"
                        onClick={() => openGame(game._id, game.name, String(game.showIcon ?? ""))}
                      >
                        <span className="thn-hot__dots" aria-hidden>
                          <i />
                          <i />
                          <i />
                        </span>
                        <img src={game.showIcon || getAssetUrl("/assets/thn/icon-slot.svg")} alt="" />
                        <span className="thn-hot__name">{game.name}</span>
                        <span className="thn-hot__mod">{"// MODULE.REDY"}</span>
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <h2 className="thn-panel__title">
                    <span className="thn-panel__bar" />
                    HỆ THỐNG NỔ HŨ
                  </h2>
                  <p className="thn-slot__lead">CHỌN SẢNH GAME</p>
                  <p className="thn-slot__sub">
                    {NH_GAME_PROVIDERS.length} SẢNH - CHỌN MỘT SẢNH ĐỂ XEM DANH SÁCH GAME VÀ TỶ LỆ
                  </p>
                  <div className="thn-hot__grid">
                    {NH_GAME_PROVIDERS.map((hall) => (
                      <button
                        key={hall.link}
                        type="button"
                        className="thn-hot__card"
                        onClick={() => setSlotHall(hall.link.replace(/^\//, ""))}
                      >
                        <span className="thn-hot__dots" aria-hidden>
                          <i />
                          <i />
                          <i />
                        </span>
                        <img src={getAssetUrl(hall.url)} alt="" />
                        <span className="thn-hot__name">{hall.name}</span>
                        <span className="thn-hot__mod">{"// MODULE.REDY"}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </section>
          ) : null}

          {tab === "card" ? (
            <section className="thn-panel thn-sexy" aria-label="Casino sexy">
              <div className="thn-sexy__head">
                <h2 className="thn-panel__title">
                  <span className="thn-panel__bar" />
                  CASINO SEXY
                </h2>
                <p className="thn-sexy__meta">
                  <b>{openHalls}</b> bàn đang mở. <b>{goodTables}</b> cầu tốt.{" "}
                  <b>{streakTables}</b> bàn đang bệt 3+
                </p>
              </div>

              <div className="thn-sexy__filters">
                <div>
                  <p className="thn-sexy__label">Lọc bàn</p>
                  <div className="thn-sexy__chips">
                    {CARD_FILTERS.map((item) => (
                      <button
                        key={item}
                        type="button"
                        className={`thn-sexy__chip${cardFilter === item ? " is-active" : ""}`}
                        onClick={() => setCardFilter(item)}
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="thn-sexy__label">Sắp xếp</p>
                  <div className="thn-sexy__chips">
                    {CARD_SORTS.map((item) => (
                      <button
                        key={item}
                        type="button"
                        className={`thn-sexy__chip${cardSort === item ? " is-active" : ""}`}
                        onClick={() => setCardSort(item)}
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="thn-sexy__grid">
                {visibleRooms.map((room) => {
                  const badge = winBadge(room.winPercent);
                  return (
                    <div
                      key={room.id}
                      role="button"
                      tabIndex={0}
                      className="thn-sexy__card"
                      style={{
                        backgroundImage: `url(${getAssetUrl("/assets/thn/card-table-bg.svg")})`,
                      }}
                      onClick={() => navigate(`/casino/room/${room.tableName}`)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          navigate(`/casino/room/${room.tableName}`);
                        }
                      }}
                    >
                      <div className="thn-sexy__card-head">
                        <span>BÀN BACCARAT {room.tableName}</span>
                        <span className="thn-sexy__live">LIVE</span>
                      </div>
                      <div className="thn-sexy__stats">
                        <span className="is-player">
                          <b>{room.player}</b>
                          PLAYER
                        </span>
                        <span className="is-tie">
                          <b>{room.tie}</b>
                          HÒA
                        </span>
                        <span className="is-banker">
                          <b>{room.banker}</b>
                          BANKER
                        </span>
                      </div>
                      <div className="thn-sexy__mid">
                        <div className="thn-sexy__board">
                          <ResultTable tableData={room.totalRound} compact />
                        </div>
                        <div className={`thn-sexy__win ${badge.cls}`}>
                          <small>{badge.label}</small>
                          <strong>
                            {badge.caption} {room.winPercent}%
                          </strong>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ) : null}
        </div>

        <aside className={`thn-verify${extLink ? " is-live" : ""}`} aria-label="Màn hình game">
          <GameScreenEmbed
            url={extLink}
            name={extName}
            title={`Game ${extName}`}
            loading={extLoading}
          />
        </aside>
      </div>
    </main>
  );
};

export default HomePage;
